// src/pages/VisiteEntrepot.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import {
  Search, Shuffle, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, X, Save, Eye,
  RotateCcw, Package, Layers, MapPin, ArrowLeft,
  FileText, ClipboardList,
} from "lucide-react";

// ─── Constantes ───────────────────────────────────────────────────────────────
const CONDITIONS = [
  { value: "excellent",            label: "Excellent",              color: "#2d6a4f", bg: "#d8f3dc" },
  { value: "very_good",            label: "Très bon",               color: "#1d6a3a", bg: "#d8f3dc" },
  { value: "good",                 label: "Bon",                    color: "#40916c", bg: "#d8f3dc" },
  { value: "fair",                 label: "Moyen",                  color: "#b5632a", bg: "#fde8d0" },
  { value: "poor",                 label: "Mauvais",                color: "#9b2226", bg: "#fcd5d5" },
  { value: "partially_restorable", label: "Partiellement restaurable", color: "#7b1e1e", bg: "#f8c4c4" },
  { value: "heavily_damaged",      label: "Très endommagé",         color: "#6d0000", bg: "#f8b4b4" },
  { value: "irreparable",          label: "Irréparable",            color: "#3d0000", bg: "#f5a0a0" },
];

const DAMAGED_VALUES = ["poor", "partially_restorable", "heavily_damaged", "irreparable"];

const RISK_LEVELS = [
  { value: "faible",  label: "Risque faible", color: "#2d6a4f" },
  { value: "moyen",   label: "Risque moyen",  color: "#b5632a" },
  { value: "eleve",   label: "Risque élevé",  color: "#9b2226" },
  { value: "urgent",  label: "URGENT",        color: "#6d0000" },
];

const conditionOrder = {
  irreparable: 0, heavily_damaged: 1, partially_restorable: 2,
  poor: 3, fair: 4, good: 5, very_good: 6, excellent: 7,
};

const conditionLabel = {
  excellent: "Excellent", very_good: "Très bon", good: "Bon", fair: "Moyen",
  poor: "Mauvais", partially_restorable: "Part. restaurable",
  heavily_damaged: "Très endommagé", irreparable: "Irréparable",
};

const riskLabel = {
  faible: "Risque faible", moyen: "Risque moyen",
  eleve: "Risque élevé", urgent: "URGENT",
};

function getConditionMeta(val) {
  return CONDITIONS.find((c) => c.value === val) || { label: "—", color: "#aaa", bg: "#f0f0f0" };
}

// ─── Badge état ───────────────────────────────────────────────────────────────
function ConditionBadge({ value }) {
  const { t } = useTranslation();
  const m = getConditionMeta(value);
  return (
    <span style={{
      background: m.bg, color: m.color,
      border: `1px solid ${m.color}55`,
      borderRadius: 3, padding: "2px 7px",
      fontSize: 10, fontWeight: 700,
      letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>{t(`warehouseVisit.conditions.${value}`, { defaultValue: m.label })}</span>
  );
}

// ─── Ligne œuvre ──────────────────────────────────────────────────────────────
function ArtworkRow({ artwork, onSaveCondition, locationRisk }) {
  const { t } = useTranslation();
  const tr = (key, options) => t(`warehouseVisit.${key}`, options);
  const [open, setOpen]           = useState(false);
  const [condition, setCondition] = useState(artwork.condition || "");
  const [notes, setNotes]         = useState(artwork.conditionNotes || "");
  const [risk, setRisk]           = useState(artwork.visit_risk || "");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  const isDamaged = DAMAGED_VALUES.includes(condition);
  const zoneIsRisky = locationRisk && locationRisk !== "faible";

  async function handleSave() {
    setSaving(true);
    await supabase.from("artworks").update({
      condition,
      conditionNotes: notes,
      visit_risk: risk,
      updated_at: new Date().toISOString(),
    }).eq("id", artwork.id);
    onSaveCondition(artwork.id, { condition, conditionNotes: notes, visit_risk: risk });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{
      background: "#fff",
      border: isDamaged ? "1.5px solid #e8a0a0" : "1px solid #e8e0d4",
      borderLeft: isDamaged ? "4px solid #9b2226" : "4px solid transparent",
      borderRadius: 4, marginBottom: 8, overflow: "hidden",
    }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer" }}
      >
        <div style={{
          width: 46, height: 46, borderRadius: 3, flexShrink: 0,
          background: "#f0ece5", overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {artwork.image_url
            ? <img src={artwork.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <Package size={17} color="#b0a090" />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "Georgia, serif", fontSize: 13, fontWeight: 600,
            color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{artwork.title || tr("common.untitled")}</div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
            {artwork.artist || "—"}{artwork.year ? ` · ${artwork.year}` : ""}
          </div>
          {artwork.inventory_number && (
            <div style={{ fontSize: 10, color: "#bbb", marginTop: 1 }}>#{artwork.inventory_number}</div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <ConditionBadge value={condition} />
          {open ? <ChevronUp size={14} color="#aaa" /> : <ChevronDown size={14} color="#aaa" />}
        </div>
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #f0ece5", padding: "12px 12px 14px" }}>

          {/* ⚠ Avertissement zone à risque */}
          {zoneIsRisky && (
            <div style={{
              background: locationRisk === "élevé" ? "#fef2f2" : "#fffbeb",
              border: `1px solid ${locationRisk === "élevé" ? "#fecaca" : "#fde68a"}`,
              borderRadius: 3, padding: "7px 10px", marginBottom: 12,
              fontSize: 11, color: locationRisk === "élevé" ? "#dc2626" : "#d97706",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <AlertTriangle size={12} />
              {tr("risk.zoneArtwork", { risk: locationRisk })}
            </div>
          )}

          <div style={{ marginBottom: 10 }}>
            <div style={sLabel}>{tr("fields.condition")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {CONDITIONS.map((c) => (
                <button key={c.value} onClick={() => setCondition(c.value)} style={{
                  padding: "4px 10px", borderRadius: 3,
                  border: `1px solid ${c.color}`,
                  background: condition === c.value ? c.color : "#fff",
                  color: condition === c.value ? "#fff" : c.color,
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}>{tr(`conditions.${c.value}`)}</button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={sLabel}>{tr("fields.riskLevel")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {RISK_LEVELS.map((r) => (
                <button key={r.value} onClick={() => setRisk(r.value)} style={{
                  padding: "4px 10px", borderRadius: 3,
                  border: `1px solid ${r.color}`,
                  background: risk === r.value ? r.color : "#fff",
                  color: risk === r.value ? "#fff" : r.color,
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}>{tr(`risks.${r.value}`)}</button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={sLabel}>{tr("fields.visitNotes")}</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={tr("fields.notesPlaceholder")}
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box",
                border: "1px solid #e0d8cc", borderRadius: 3,
                padding: "7px 9px", fontSize: 12,
                fontFamily: "Georgia, serif", color: "#1a1a1a",
                background: "#faf8f5", resize: "vertical", outline: "none",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={{
              flex: 1, padding: "8px 0",
              background: saved ? "#2d6a4f" : "#1a1a1a",
              color: "#fff", border: "none", borderRadius: 3,
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              transition: "background 0.2s",
            }}>
              {saved ? <><CheckCircle size={13} /> {tr("actions.saved")}</> : saving ? "…" : <><Save size={13} /> {tr("actions.save")}</>}
            </button>
            <button
              onClick={() => window.open(`/artwork/${artwork.id}`, "_blank")}
              style={{
                padding: "8px 14px", background: "#fff", color: "#1a1a1a",
                border: "1px solid #1a1a1a", borderRadius: 3,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
              }}
            >
              <Eye size={13} /> {tr("actions.record")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal aléatoire ──────────────────────────────────────────────────────────
function RandomModal({ artwork, onClose, onNext, onSaveCondition }) {
  const { t, i18n } = useTranslation();
  const tr = (key, options) => t(`warehouseVisit.${key}`, options);
  const [condition, setCondition] = useState(artwork?.condition || "");
  const [notes, setNotes]         = useState(artwork?.conditionNotes || "");
  const [risk, setRisk]           = useState(artwork?.visit_risk || "");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    setCondition(artwork?.condition || "");
    setNotes(artwork?.conditionNotes || "");
    setRisk(artwork?.visit_risk || "");
    setSaved(false);
  }, [artwork?.condition, artwork?.conditionNotes, artwork?.visit_risk]);

  async function handleSave() {
    setSaving(true);
    await supabase.from("artworks").update({
      condition, conditionNotes: notes, visit_risk: risk,
      updated_at: new Date().toISOString(),
    }).eq("id", artwork.id);
    onSaveCondition(artwork.id, { condition, conditionNotes: notes, visit_risk: risk });
    setSaving(false);
    setSaved(true);
  }

  if (!artwork) return null;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
        zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div style={{
        background: "#faf8f5", borderRadius: "8px 8px 0 0",
        width: "100%", maxWidth: 480, maxHeight: "90vh",
        overflowY: "auto", padding: "20px 18px 32px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 9, color: "#c9a961", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 4 }}>
              {tr("random.title")}
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
              {artwork.title || tr("common.untitled")}
            </div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
              {artwork.artist}{artwork.year ? ` · ${artwork.year}` : ""}
              {artwork.inventory_number && <span style={{ color: "#bbb", marginLeft: 6 }}>#{artwork.inventory_number}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
            <X size={18} color="#888" />
          </button>
        </div>

        {artwork.image_url && (
          <div style={{ borderRadius: 4, overflow: "hidden", marginBottom: 14, maxHeight: 200, background: "#e8e0d4" }}>
            <img src={artwork.image_url} alt="" style={{ width: "100%", objectFit: "cover", maxHeight: 200 }} />
          </div>
        )}

        <div style={{
          background: "#fff", border: "1px solid #e8e0d4", borderRadius: 4,
          padding: "10px 12px", marginBottom: 14,
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px",
        }}>
          {artwork.medium && <div><span style={sMeta}>{tr("fields.medium")}</span><div style={sVal}>{artwork.medium}</div></div>}
          {artwork.dimensions && <div><span style={sMeta}>{tr("fields.dimensions")}</span><div style={sVal}>{artwork.dimensions}</div></div>}
          {artwork.estimated_value && (
            <div><span style={sMeta}>{tr("fields.estimatedValue")}</span>
              <div style={sVal}>{Number(artwork.estimated_value).toLocaleString(i18n.resolvedLanguage || i18n.language)} €</div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={sLabel}>{tr("fields.condition")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {CONDITIONS.map((c) => (
              <button key={c.value} onClick={() => setCondition(c.value)} style={{
                padding: "5px 11px", borderRadius: 3,
                border: `1px solid ${c.color}`,
                background: condition === c.value ? c.color : "#fff",
                color: condition === c.value ? "#fff" : c.color,
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>{tr(`conditions.${c.value}`)}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={sLabel}>{tr("fields.riskLevel")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {RISK_LEVELS.map((r) => (
              <button key={r.value} onClick={() => setRisk(r.value)} style={{
                padding: "5px 11px", borderRadius: 3,
                border: `1px solid ${r.color}`,
                background: risk === r.value ? r.color : "#fff",
                color: risk === r.value ? "#fff" : r.color,
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>{tr(`risks.${r.value}`)}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={sLabel}>{tr("fields.visitNotes")}</div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={tr("fields.randomNotesPlaceholder")}
            rows={3}
            style={{
              width: "100%", boxSizing: "border-box",
              border: "1px solid #e0d8cc", borderRadius: 3,
              padding: "7px 9px", fontSize: 12,
              fontFamily: "Georgia, serif", color: "#1a1a1a",
              background: "#fff", resize: "vertical", outline: "none",
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: "10px 0",
            background: saved ? "#2d6a4f" : "#1a1a1a",
            color: "#fff", border: "none", borderRadius: 3,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            transition: "background 0.2s",
          }}>
            {saved ? `✓ ${tr("actions.saved")}` : saving ? "…" : tr("actions.save")}
          </button>
          <button onClick={onNext} style={{
            flex: 1, padding: "10px 0", background: "#fff", color: "#c9a961",
            border: "1.5px solid #c9a961", borderRadius: 3,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <Shuffle size={14} /> {tr("actions.next")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Styles partagés ──────────────────────────────────────────────────────────
const sLabel = { fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 7 };
const sMeta  = { fontSize: 9,  color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 2 };
const sVal   = { fontSize: 12, color: "#1a1a1a", fontFamily: "Georgia, serif" };

// ─── Bouton filtre ────────────────────────────────────────────────────────────
function FilterBtn({ active, danger, gold, onClick, children }) {
  const border = active && danger ? "#9b2226" : active ? "#1a1a1a" : gold ? "#c9a961" : "#e0d8cc";
  const bg     = active && danger ? "#9b2226" : active ? "#1a1a1a" : "#fff";
  const color  = active ? "#fff" : danger ? "#9b2226" : gold ? "#c9a961" : "#555";
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: "7px 4px",
      border: `1px solid ${border}`,
      borderRadius: 4, fontSize: 11, fontWeight: 600,
      cursor: "pointer", background: bg, color,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
    }}>
      {children}
    </button>
  );
}

// ─── Génération rapport PDF ───────────────────────────────────────────────────
function buildReportHTML(loc, artworks, date, translate, locale = "fr") {
  const copy = (key, options = {}) => translate(key, options);
  const visitDate = date.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  const evaluated = artworks.filter(a => a.condition).length;
  const urgentList = artworks
    .filter(a => DAMAGED_VALUES.includes(a.condition) || ["urgent", "eleve"].includes(a.visit_risk))
    .sort((a, b) => (conditionOrder[a.condition] ?? 8) - (conditionOrder[b.condition] ?? 8));
  const damaged = artworks.filter(a => DAMAGED_VALUES.includes(a.condition)).length;
  const highRisk = artworks.filter(a => ["urgent", "eleve"].includes(a.visit_risk)).length;

  return `<!DOCTYPE html>
<html lang="${String(locale).slice(0, 2)}">
<head>
  <meta charset="UTF-8">
  <title>${copy("pdf.title")} — ${loc?.name} — ${visitDate}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Georgia, serif; color: #1a1a1a; background: #fff; padding: 48px 56px; max-width: 800px; margin: 0 auto; }
    .header-label { font-size: 10px; color: #c9a961; text-transform: uppercase; letter-spacing: 0.14em; font-weight: 700; margin-bottom: 8px; font-family: Arial, sans-serif; }
    h1 { font-size: 30px; font-weight: 600; margin-bottom: 4px; }
    .meta { font-size: 13px; color: #666; margin-bottom: 4px; font-family: Arial, sans-serif; }
    .divider { border: none; border-top: 1px solid #e8e0d4; margin: 24px 0; }
    .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 24px 0; }
    .stat-box { border: 1px solid #e0d8cc; border-radius: 4px; padding: 16px; text-align: center; }
    .stat-num { font-size: 30px; font-weight: 700; }
    .stat-label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 5px; font-family: Arial, sans-serif; }
    .section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #c9a961; margin: 32px 0 14px; font-family: Arial, sans-serif; border-bottom: 1px solid #e8e0d4; padding-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
    th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #999; text-align: left; padding: 8px 10px; border-bottom: 2px solid #e8e0d4; font-family: Arial, sans-serif; }
    td { font-size: 12px; padding: 10px; border-bottom: 1px solid #f0ece5; vertical-align: top; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; font-family: Arial, sans-serif; letter-spacing: 0.04em; text-transform: uppercase; }
    .urgent-card { border: 1px solid #e8a0a0; border-left: 4px solid #9b2226; border-radius: 4px; padding: 14px 16px; margin-bottom: 12px; }
    .urgent-title { font-size: 15px; font-weight: 600; margin-bottom: 3px; }
    .urgent-meta { font-size: 11px; color: #888; margin-bottom: 10px; font-family: Arial, sans-serif; }
    .notes-box { font-size: 12px; color: #555; font-style: italic; margin-top: 10px; background: #faf8f5; padding: 9px 11px; border-radius: 3px; border-left: 3px solid #e8e0d4; }
    .zone-alert { background: #fef2f2; border: 1px solid #fecaca; border-radius: 4px; padding: 11px 14px; margin: 18px 0; font-size: 12px; color: #dc2626; font-family: Arial, sans-serif; }
    .zone-alert-moyen { background: #fffbeb; border-color: #fde68a; color: #d97706; }
    .no-urgent { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 4px; padding: 20px; text-align: center; color: #2d6a4f; font-size: 14px; margin: 16px 0; }
    .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e8e0d4; font-size: 10px; color: #bbb; text-align: center; font-family: Arial, sans-serif; letter-spacing: 0.04em; }
    .c-irreparable          { background: #f5a0a0; color: #3d0000; }
    .c-heavily_damaged      { background: #f8b4b4; color: #6d0000; }
    .c-partially_restorable { background: #f8c4c4; color: #7b1e1e; }
    .c-poor                 { background: #fcd5d5; color: #9b2226; }
    .c-fair                 { background: #fde8d0; color: #b5632a; }
    .c-good                 { background: #d8f3dc; color: #40916c; }
    .c-very_good            { background: #d8f3dc; color: #1d6a3a; }
    .c-excellent            { background: #d8f3dc; color: #2d6a4f; }
    .r-urgent { background: #f8b4b4; color: #6d0000; }
    .r-eleve  { background: #fcd5d5; color: #9b2226; }
    .r-moyen  { background: #fde8d0; color: #b5632a; }
    .r-faible { background: #d8f3dc; color: #2d6a4f; }
    @media print {
      body { padding: 20px 28px; }
      .stats-grid { grid-template-columns: repeat(4, 1fr); }
    }
  </style>
</head>
<body>
  <div class="header-label">Artkiv · ${copy("pdf.title")}</div>
  <h1>${loc?.name || copy("pdf.location")}</h1>
  ${loc?.address ? `<div class="meta">📍 ${loc.address}</div>` : ""}
  ${loc?.temperature || loc?.humidity ? `<div class="meta" style="margin-top:6px;">
    ${loc.temperature ? `🌡 ${loc.temperature}°C` : ""}
    ${loc.temperature && loc.humidity ? " &nbsp;·&nbsp; " : ""}
    ${loc.humidity ? `💧 ${loc.humidity}%` : ""}
  </div>` : ""}
  <div class="meta" style="margin-top:8px; color:#1a1a1a; font-weight:600;">${copy("pdf.visitDate")} ${visitDate}</div>

  ${loc?.storage_risk && loc.storage_risk !== "faible" ? `
  <div class="zone-alert${loc.storage_risk === "moyen" || loc.storage_risk === "Risque moyen" ? " zone-alert-moyen" : ""}">
    ⚠ ${copy("pdf.riskZone", { risk: loc.storage_risk })}${loc.storage_notes ? ` — ${loc.storage_notes}` : ` — ${copy("pdf.unsuitable")}`}
  </div>` : ""}

  ${Array.isArray(loc?.storage_photos) && loc.storage_photos.length > 0 ? `
  <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:16px;">
    ${loc.storage_photos.map((url) => `<img src="${url}" style="width:120px;height:120px;object-fit:cover;border-radius:4px;border:1px solid #e8e0d4;">`).join("")}
  </div>` : ""}

  <hr class="divider">

  <div class="stats-grid">
    <div class="stat-box">
      <div class="stat-num">${artworks.length}</div>
      <div class="stat-label">${copy("stats.present")}</div>
    </div>
    <div class="stat-box">
      <div class="stat-num" style="color:${evaluated === artworks.length ? "#2d6a4f" : "#b5632a"}">${evaluated}</div>
      <div class="stat-label">${copy("pdf.evaluatedVisit")}</div>
    </div>
    <div class="stat-box" style="border-color:#e8a0a0;">
      <div class="stat-num" style="color:#9b2226;">${damaged}</div>
      <div class="stat-label">${copy("stats.damaged")}</div>
    </div>
    <div class="stat-box" style="border-color:#fde68a;">
      <div class="stat-num" style="color:#d97706;">${highRisk}</div>
      <div class="stat-label">${copy("stats.highRisk")}</div>
    </div>
  </div>

  <div class="section-title">${copy("pdf.intervention")}</div>
  ${urgentList.length === 0
    ? `<div class="no-urgent">✓ ${copy("pdf.noUrgency")}</div>`
    : urgentList.map(a => `
  <div class="urgent-card">
    <div class="urgent-title">${a.title || copy("common.untitled")}</div>
    <div class="urgent-meta">${a.artist || "—"}${a.year ? ` · ${a.year}` : ""}${a.inventory_number ? ` · N° ${a.inventory_number}` : ""}</div>
    <div style="display:flex;gap:7px;flex-wrap:wrap;">
      ${a.condition ? `<span class="badge c-${a.condition}">${copy(`conditions.${a.condition}`, { defaultValue: conditionLabel[a.condition] || a.condition })}</span>` : ""}
      ${a.visit_risk ? `<span class="badge r-${a.visit_risk}">${copy(`risks.${a.visit_risk}`, { defaultValue: riskLabel[a.visit_risk] || a.visit_risk })}</span>` : ""}
    </div>
    ${a.conditionNotes ? `<div class="notes-box">${a.conditionNotes}</div>` : ""}
  </div>`).join("")}

  <div class="section-title">${copy("pdf.fullInventory")}</div>
  <table>
    <thead>
      <tr>
        <th>${copy("pdf.artwork")}</th>
        <th>${copy("pdf.artist")}</th>
        <th>${copy("pdf.inventoryNumber")}</th>
        <th>${copy("fields.condition")}</th>
        <th>${copy("fields.riskLevel")}</th>
      </tr>
    </thead>
    <tbody>
      ${artworks.map(a => `
      <tr>
        <td>${a.title || copy("common.untitled")}</td>
        <td>${a.artist || "—"}${a.year ? `<br><span style="font-size:10px;color:#aaa;">${a.year}</span>` : ""}</td>
        <td style="color:#bbb;font-size:11px;">${a.inventory_number || "—"}</td>
        <td>${a.condition ? `<span class="badge c-${a.condition}">${copy(`conditions.${a.condition}`, { defaultValue: conditionLabel[a.condition] })}</span>` : `<span style="color:#ccc;font-size:11px;">—</span>`}</td>
        <td>${a.visit_risk ? `<span class="badge r-${a.visit_risk}">${copy(`risks.${a.visit_risk}`, { defaultValue: riskLabel[a.visit_risk] })}</span>` : `<span style="color:#ccc;font-size:11px;">—</span>`}</td>
      </tr>`).join("")}
    </tbody>
  </table>

  <div class="footer">
    ARTKIV · ${copy("pdf.generatedOn", { date: visitDate })} · ${copy("pdf.confidential")}
  </div>
</body>
</html>`;
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function VisiteEntrepot() {
  const { t, i18n } = useTranslation();
  const tr = (key, options) => t(`warehouseVisit.${key}`, options);
  const [step, setStep]             = useState("client"); // "client" | "select" | "inspect" | "summary"
  const [clients, setClients]       = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [, setIsExpert]     = useState(false);
  const [, setSelectedClientId] = useState(null);
  const [locations, setLocations]   = useState([]);
  const [loadingLocs, setLoadingLocs] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [artworks, setArtworks]     = useState([]);
  const [loadingArt, setLoadingArt] = useState(false);
  const [search, setSearch]         = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [randomArtwork, setRandomArtwork] = useState(null);
  const [showRandom, setShowRandom] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const galleryPhotoInputRef = React.useRef(null);
  const cameraPhotoInputRef = React.useRef(null);

  // Chargement initial : rôle + clients accessibles
  useEffect(() => {
    async function fetchClients() {
      setLoadingClients(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) { setLoadingClients(false); return; }

      const { data: profile } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();

      if (profile?.role === "expert") {
        setIsExpert(true);
        const { data: perms } = await supabase
          .from("collection_permissions")
          .select("client_id")
          .eq("grantee_id", user.id);

        const clientIds = [...new Set((perms || []).map(p => p.client_id).filter(Boolean))];
        let list = [];
        if (clientIds.length > 0) {
          const { data: profilesData } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .in("id", clientIds);
          list = (profilesData || []).map(c => ({
            id: c.id,
            name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || c.id,
            email: c.email,
          }));
        }
        setClients(list);
        // Si un seul client, sauter direct à la sélection d'emplacement
        if (list.length === 1) {
          setSelectedClientId(list[0].id);
          setStep("select");
          fetchLocationsForClient(list[0].id);
        }
      } else {
        // Collecteur : pas de sélection client, charger ses propres emplacements
        setIsExpert(false);
        fetchLocationsForClient(user.id);
        setStep("select");
      }
      setLoadingClients(false);
    }
    fetchClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchLocationsForClient(clientId) {
    setLoadingLocs(true);
    setLocations([]);
    const { data, error } = await supabase
      .from("locations")
      .select("*")
      .eq("client_id", clientId)
      .order("name");
    if (!error && data) setLocations(data);
    setLoadingLocs(false);
  }

  function handleSelectClient(client) {
    setSelectedClientId(client.id);
    setStep("select");
    fetchLocationsForClient(client.id);
  }

  async function loadArtworks(locationId) {
    setLoadingArt(true);
    const AW_SELECT = "id, title, artist, year, medium, dimensions, condition, conditionNotes, visit_risk, image_url, inventory_number, estimated_value, location_id, updated_at";
    const { data: expertData, error: expertError } = await supabase
      .from("v_expert_artworks")
      .select(AW_SELECT)
      .eq("location_id", locationId)
      .order("title");
    if (!expertError && expertData && expertData.length > 0) {
      setArtworks(expertData);
      setLoadingArt(false);
      return;
    }
    const { data, error } = await supabase
      .from("artworks")
      .select(AW_SELECT)
      .eq("location_id", locationId)
      .order("title");
    if (!error && data) setArtworks(data);
    setLoadingArt(false);
  }

  function handleSelectLocation(loc) {
    setSelectedLoc(loc);
    setArtworks([]);
    setSearch("");
    setFilterMode("all");
    setStep("inspect");
    loadArtworks(loc.id);
  }

  function handleBack() {
    if (step === "inspect" || step === "summary") {
      setStep("select");
      setSelectedLoc(null);
      setArtworks([]);
      setShowRandom(false);
    } else {
      setStep("client");
      setSelectedClientId(null);
      setLocations([]);
    }
  }

  function handleSaveCondition(id, updates) {
    setArtworks((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));
  }

  // Photos du lieu — la base avait déjà `locations.storage_photos` (jsonb)
  // prévu pour ça, mais rien dans l'écran de visite ne l'exploitait : seuls
  // les états/risques des œuvres étaient modifiables. Retour terrain 11/09/2026.
  async function uploadLocationPhoto(file) {
    if (!selectedLoc || !file) return;
    setUploadingPhoto(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const filePath = `${selectedLoc.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("warehouse-visit-photos")
        .upload(filePath, file, { upsert: true });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from("warehouse-visit-photos").getPublicUrl(filePath);
      const url = pub?.publicUrl;
      if (!url) return;

      const nextPhotos = [...(selectedLoc.storage_photos || []), url];
      const { error: updErr } = await supabase
        .from("locations")
        .update({ storage_photos: nextPhotos })
        .eq("id", selectedLoc.id);
      if (updErr) throw updErr;

      setSelectedLoc((prev) => ({ ...prev, storage_photos: nextPhotos }));
    } catch (e) {
      console.error("[VisiteEntrepot] uploadLocationPhoto failed", e);
      alert(tr("location.photoUploadError", { defaultValue: "Erreur lors de l'envoi de la photo." }));
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function removeLocationPhoto(url) {
    if (!selectedLoc) return;
    const nextPhotos = (selectedLoc.storage_photos || []).filter((p) => p !== url);
    const { error } = await supabase.from("locations").update({ storage_photos: nextPhotos }).eq("id", selectedLoc.id);
    if (!error) setSelectedLoc((prev) => ({ ...prev, storage_photos: nextPhotos }));
  }

  async function handleFinishVisit() {
    // Enregistrer la date de visite sur l'emplacement
    await supabase
      .from("locations")
      .update({ visited_at: new Date().toISOString().split("T")[0] })
      .eq("id", selectedLoc.id);
    setStep("summary");
  }

  function generateVisitPDF() {
    const html = buildReportHTML(selectedLoc, artworks, new Date(), tr, i18n.resolvedLanguage || i18n.language || "fr");
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  }

  const stats = {
    total:        artworks.length,
    nonEvalue:    artworks.filter((a) => !a.condition).length,
    moyen:        artworks.filter((a) => a.condition === "fair").length,
    mauvais:      artworks.filter((a) => a.condition === "poor").length,
    tres_mauvais: artworks.filter((a) => ["heavily_damaged", "irreparable", "partially_restorable"].includes(a.condition)).length,
    get damaged() { return this.mauvais + this.tres_mauvais; },
  };

  const filtered = artworks
    .filter((a) => filterMode === "damaged" ? DAMAGED_VALUES.includes(a.condition) : true)
    .filter((a) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (a.title || "").toLowerCase().includes(q)
        || (a.artist || "").toLowerCase().includes(q)
        || (a.inventory_number || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (filterMode === "damaged")
        return (conditionOrder[a.condition] ?? 5) - (conditionOrder[b.condition] ?? 5);
      return (a.title || "").localeCompare(b.title || "", "fr");
    });

  function pickRandom() {
    if (artworks.length === 0) return;
    setRandomArtwork(artworks[Math.floor(Math.random() * artworks.length)]);
    setShowRandom(true);
  }

  // ── ÉCRAN 0 : sélection client (experts uniquement) ───────────────────────
  if (step === "client") {
    return (
      <div style={{ background: "#f9f7f4", minHeight: "100vh" }}>
        <div style={{ background: "#1a1a1a", padding: "18px 18px 16px" }}>
          <div style={{ fontSize: 9, color: "#c9a961", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: 4 }}>
            Artkiv · {tr("title")}
          </div>
          <div style={{ fontFamily: "Georgia, serif", color: "#fff", fontSize: 20, fontWeight: 600 }}>
            {tr("client.title")}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            {tr("client.description")}
          </div>
        </div>
        <div style={{ padding: "16px 14px" }}>
          {loadingClients ? (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa", fontSize: 13 }}>{tr("common.loading")}</div>
          ) : clients.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
              <div style={{ fontSize: 13 }}>{tr("client.empty")}</div>
            </div>
          ) : clients.map((client) => (
            <button
              key={client.id}
              onClick={() => handleSelectClient(client)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: "#fff", border: "1px solid #e8e0d4",
                borderRadius: 4, padding: "14px 16px", marginBottom: 10,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#c9a961"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e8e0d4"; }}
            >
              <div style={{ fontFamily: "Georgia, serif", fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>
                {client.name}
              </div>
              {client.email && (
                <div style={{ fontSize: 11, color: "#999", marginTop: 3 }}>{client.email}</div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── ÉCRAN 1 : sélection emplacement ────────────────────────────────────────
  if (step === "select") {
    return (
      <div style={{ background: "#f9f7f4", minHeight: "100vh" }}>
        <div style={{ background: "#1a1a1a", padding: "18px 18px 16px" }}>
          <div style={{ fontSize: 9, color: "#c9a961", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: 4 }}>
            Artkiv · {tr("title")}
          </div>
          <div style={{ fontFamily: "Georgia, serif", color: "#fff", fontSize: 20, fontWeight: 600 }}>
            {tr("location.title")}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            {tr("location.description")}
          </div>
        </div>

        <div style={{ padding: "16px 14px" }}>
          {loadingLocs ? (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa", fontSize: 13 }}>
              {tr("location.loading")}
            </div>
          ) : locations.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
              <MapPin size={32} color="#d0c8bc" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 13 }}>{tr("location.empty")}</div>
            </div>
          ) : locations.map((loc) => (
            <button
              key={loc.id}
              onClick={() => handleSelectLocation(loc)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: "#fff", border: "1px solid #e8e0d4",
                borderRadius: 4, padding: "14px 16px", marginBottom: 10,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#c9a961"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e8e0d4"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>
                  {loc.name}
                </div>
                {loc.storage_risk && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2,
                    color: loc.storage_risk === "élevé" ? "#dc2626" : loc.storage_risk === "moyen" ? "#d97706" : "#16a34a",
                    background: loc.storage_risk === "élevé" ? "#fef2f2" : loc.storage_risk === "moyen" ? "#fffbeb" : "#f0fdf4",
                    border: `1px solid ${loc.storage_risk === "élevé" ? "#fecaca" : loc.storage_risk === "moyen" ? "#fde68a" : "#bbf7d0"}`,
                  }}>
                    ⚠ {loc.storage_risk}
                  </span>
                )}
              </div>

              {loc.type && <div style={{ fontSize: 11, color: "#999", marginTop: 3 }}>{loc.type}</div>}

              {loc.address && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                  <MapPin size={11} color="#bbb" />{loc.address}
                </div>
              )}

              {(loc.temperature || loc.humidity) && (
                <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                  {loc.temperature && <span style={{ fontSize: 11, color: "#aaa" }}>🌡 {loc.temperature} °C</span>}
                  {loc.humidity    && <span style={{ fontSize: 11, color: "#aaa" }}>💧 {loc.humidity} %</span>}
                </div>
              )}

              {loc.visited_at && (
                <div style={{ fontSize: 11, color: "#bbb", marginTop: 6 }}>
                  {tr("location.lastVisit")} : {new Date(loc.visited_at).toLocaleDateString(i18n.resolvedLanguage || i18n.language, { day: "numeric", month: "long", year: "numeric" })}
                </div>
              )}

              {loc.notes && (
                <div style={{
                  fontSize: 11, color: "#999", marginTop: 8,
                  borderTop: "1px solid #f0ece5", paddingTop: 8, fontStyle: "italic",
                }}>{loc.notes}</div>
              )}

              <div style={{ marginTop: 12, fontSize: 11, color: "#c9a961", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                {tr("actions.inspect")} →
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── ÉCRAN 3 : résumé post-visite ────────────────────────────────────────────
  if (step === "summary") {
    const evaluated   = artworks.filter(a => a.condition).length;
    const damaged     = artworks.filter(a => DAMAGED_VALUES.includes(a.condition)).length;
    const highRisk    = artworks.filter(a => ["urgent", "eleve"].includes(a.visit_risk)).length;
    const urgentList  = artworks
      .filter(a => DAMAGED_VALUES.includes(a.condition) || ["urgent", "eleve"].includes(a.visit_risk))
      .sort((a, b) => (conditionOrder[a.condition] ?? 8) - (conditionOrder[b.condition] ?? 8));
    const visitDate   = new Date().toLocaleDateString(i18n.resolvedLanguage || i18n.language, { day: "numeric", month: "long", year: "numeric" });

    return (
      <div style={{ background: "#f9f7f4", minHeight: "100vh" }}>

        {/* Header */}
        <div style={{ background: "#1a1a1a", padding: "18px 18px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <button onClick={() => setStep("inspect")} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
              <ArrowLeft size={17} color="#c9a961" />
            </button>
            <div style={{ fontSize: 9, color: "#c9a961", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
              {tr("summary.completed")} · {selectedLoc?.name}
            </div>
          </div>
          <div style={{ fontFamily: "Georgia, serif", color: "#fff", fontSize: 20, fontWeight: 600 }}>
            {tr("summary.title")}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>{visitDate}</div>
        </div>

        <div style={{ padding: "16px 14px 100px" }}>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[
              { label: tr("stats.present"),      value: artworks.length, color: "#1a1a1a" },
              { label: tr("stats.evaluated"),   value: `${evaluated}/${artworks.length}`, color: evaluated === artworks.length ? "#2d6a4f" : "#b5632a" },
              { label: tr("stats.damaged"),     value: damaged,   color: damaged > 0 ? "#9b2226" : "#aaa" },
              { label: tr("stats.highRisk"),    value: highRisk,  color: highRisk > 0 ? "#d97706" : "#aaa" },
            ].map(s => (
              <div key={s.label} style={{ background: "#fff", border: "1px solid #e8e0d4", borderRadius: 4, padding: "16px" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 5 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Zone à risque */}
          {selectedLoc?.storage_risk && selectedLoc.storage_risk !== "faible" && (
            <div style={{
              background: selectedLoc.storage_risk === "élevé" ? "#fef2f2" : "#fffbeb",
              border: `1px solid ${selectedLoc.storage_risk === "élevé" ? "#fecaca" : "#fde68a"}`,
              borderRadius: 4, padding: "10px 14px", marginBottom: 18,
              fontSize: 12, color: selectedLoc.storage_risk === "élevé" ? "#dc2626" : "#d97706",
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong>{tr("risk.zone", { risk: selectedLoc.storage_risk })}</strong>
                {selectedLoc.storage_notes && <div style={{ marginTop: 3, fontWeight: 400 }}>{selectedLoc.storage_notes}</div>}
              </div>
            </div>
          )}

          {/* Urgences */}
          {urgentList.length > 0 ? (
            <>
              <div style={{ fontSize: 10, color: "#c9a961", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangle size={11} />
                {tr("summary.priority", { count: urgentList.length })}
              </div>
              {urgentList.map(a => (
                <div key={a.id} style={{
                  background: "#fff",
                  border: "1.5px solid #e8a0a0",
                  borderLeft: "4px solid #9b2226",
                  borderRadius: 4, padding: "12px 14px", marginBottom: 8,
                }}>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                    {a.title || tr("common.untitled")}
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    {a.artist || "—"}{a.year ? ` · ${a.year}` : ""}{a.inventory_number ? ` · #${a.inventory_number}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    {a.condition && <ConditionBadge value={a.condition} />}
                    {a.visit_risk && (
                      <span style={{
                        background: a.visit_risk === "urgent" ? "#f8b4b4" : "#fde8d0",
                        color: a.visit_risk === "urgent" ? "#6d0000" : "#b5632a",
                        border: `1px solid ${a.visit_risk === "urgent" ? "#e8a0a0" : "#f4c89a"}`,
                        borderRadius: 3, padding: "2px 7px",
                        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                      }}>
                        {tr(`risks.${a.visit_risk}`, { defaultValue: riskLabel[a.visit_risk] || a.visit_risk })}
                      </span>
                    )}
                  </div>
                  {a.conditionNotes && (
                    <div style={{ fontSize: 11, color: "#666", marginTop: 8, fontStyle: "italic", background: "#faf8f5", padding: "7px 9px", borderRadius: 3, borderLeft: "3px solid #e8e0d4" }}>
                      {a.conditionNotes}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "28px 20px", background: "#fff", borderRadius: 4, border: "1px solid #e8e0d4" }}>
              <CheckCircle size={32} color="#2d6a4f" style={{ marginBottom: 10 }} />
              <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#2d6a4f", fontWeight: 600 }}>
                {tr("summary.noUrgency")}
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {tr("summary.allGood")}
              </div>
            </div>
          )}
        </div>

        {/* Footer fixe */}
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "#fff", borderTop: "1px solid #e8e0d4",
          padding: "12px 14px", display: "flex", gap: 10,
        }}>
          <button onClick={() => setStep("inspect")} style={{
            flex: 1, padding: "12px 0", background: "#fff", color: "#1a1a1a",
            border: "1px solid #1a1a1a", borderRadius: 4,
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <ArrowLeft size={13} /> {tr("actions.back")}
          </button>
          <button onClick={generateVisitPDF} style={{
            flex: 2, padding: "12px 0", background: "#1a1a1a", color: "#fff",
            border: "none", borderRadius: 4,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          }}>
            <FileText size={14} /> {tr("actions.exportPdf")}
          </button>
        </div>
      </div>
    );
  }

  // ── ÉCRAN 2 : inspection ────────────────────────────────────────────────────
  const zoneIsRisky = selectedLoc?.storage_risk && selectedLoc.storage_risk !== "faible";
  const evaluatedCount = artworks.filter(a => a.condition).length;

  return (
    <div style={{ background: "#f9f7f4", minHeight: "100vh" }}>
      <style>{`
        .visite-inner { max-width: 1200px; margin: 0 auto; padding: 0 14px; }
        .visite-artwork-grid { display: grid; grid-template-columns: 1fr; gap: 0; }
        .visite-searchrow { display: flex; gap: 12px; align-items: center; }
        .visite-searchrow .visite-search-wrap { flex: 1; }
        .visite-filters { display: flex; gap: 7px; flex-shrink: 0; }
        @media (min-width: 900px) {
          .visite-inner { padding: 0 32px; }
          .visite-artwork-grid { grid-template-columns: 1fr 1fr; gap: 0 16px; }
          .visite-searchrow { flex-direction: row; }
          .visite-searchrow .visite-search-wrap { min-width: 300px; }
        }
      `}</style>

      {/* Header */}
      <div style={{ background: "#1a1a1a", padding: "12px 0 10px", position: "sticky", top: 0, zIndex: 100 }}>
        <div className="visite-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <button onClick={handleBack} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
              <ArrowLeft size={18} color="#c9a961" />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "#c9a961", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
                {tr("inspection.title")} · {selectedLoc?.name}
              </div>
              {selectedLoc?.address && (
                <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>{selectedLoc.address}</div>
              )}
            </div>
            <button onClick={() => loadArtworks(selectedLoc.id)} style={{ background: "none", border: "none", cursor: "pointer" }}>
              <RotateCcw size={15} color="#555" />
            </button>
          </div>

          {!loadingArt && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
              {[
                { label: tr("stats.total"),       value: stats.total,        color: "#fff"    },
                { label: tr("stats.notEvaluated"), value: stats.nonEvalue,  color: "#888"    },
                { label: tr("conditions.fair"),  value: stats.moyen,        color: "#f4a261" },
                { label: tr("conditions.poor"),  value: stats.mauvais,      color: "#e07070" },
                { label: tr("stats.damagedShort"), value: stats.tres_mauvais, color: "#c0392b" },
              ].map((s) => (
                <div key={s.label} style={{ background: "#2a2a2a", borderRadius: 4, padding: "5px 14px", textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: s.color, fontFamily: "Georgia, serif" }}>{s.value}</div>
                  <div style={{ fontSize: 9, color: "#555", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Photos du lieu — documenter la visite elle-même (état général, rangement,
          conditions), distinct des états par œuvre gérés plus bas. */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e0d4", padding: "12px 0" }}>
        <div className="visite-inner">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
              {tr("location.photosTitle", { defaultValue: "Photos du lieu" })}
              {selectedLoc?.storage_photos?.length > 0 && ` (${selectedLoc.storage_photos.length})`}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                ref={cameraPhotoInputRef} type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLocationPhoto(f); e.target.value = ""; }}
              />
              <input
                ref={galleryPhotoInputRef} type="file" accept="image/*" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLocationPhoto(f); e.target.value = ""; }}
              />
              <button
                type="button" disabled={uploadingPhoto} onClick={() => cameraPhotoInputRef.current?.click()}
                style={{ padding: "6px 12px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
              >
                📷 {tr("location.takePhoto", { defaultValue: "Prendre une photo" })}
              </button>
              <button
                type="button" disabled={uploadingPhoto} onClick={() => galleryPhotoInputRef.current?.click()}
                style={{ padding: "6px 12px", background: "#fff", color: "#1a1a1a", border: "1px solid #1a1a1a", borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
              >
                {uploadingPhoto ? "…" : tr("location.chooseFromGallery", { defaultValue: "Galerie" })}
              </button>
            </div>
          </div>
          {selectedLoc?.storage_photos?.length > 0 && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
              {selectedLoc.storage_photos.map((url) => (
                <div key={url} style={{ position: "relative", flexShrink: 0 }}>
                  <img src={url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 4, border: "1px solid #e8e0d4" }} />
                  <button
                    type="button" onClick={() => removeLocationPhoto(url)}
                    style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#1a1a1a", color: "#fff", border: "1.5px solid #fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bannière zone à risque */}
      {zoneIsRisky && (
        <div style={{
          background: selectedLoc.storage_risk === "élevé" ? "#fef2f2" : "#fffbeb",
          borderBottom: `1px solid ${selectedLoc.storage_risk === "élevé" ? "#fecaca" : "#fde68a"}`,
          padding: "9px 0",
        }}>
          <div className="visite-inner" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: selectedLoc.storage_risk === "élevé" ? "#dc2626" : "#d97706" }}>
            <AlertTriangle size={14} style={{ flexShrink: 0 }} />
            <span>
              <strong>{tr("risk.zone", { risk: selectedLoc.storage_risk })}</strong>
              {selectedLoc.storage_notes
                ? ` — ${selectedLoc.storage_notes}`
                : ` — ${tr("risk.unsuitable")}`}
            </span>
          </div>
        </div>
      )}

      {/* Barre recherche + filtres */}
      <div style={{ padding: "10px 0", background: "#fff", borderBottom: "1px solid #e8e0d4", position: "sticky", top: 110, zIndex: 90 }}>
        <div className="visite-inner visite-searchrow">
          <div className="visite-search-wrap" style={{ position: "relative" }}>
            <Search size={14} color="#aaa" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }} />
            <input
              type="text"
              placeholder={tr("inspection.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "8px 30px 8px 28px",
                border: "1px solid #e0d8cc", borderRadius: 4,
                fontSize: 13, background: "#faf8f5", color: "#1a1a1a", outline: "none",
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}>
                <X size={13} color="#aaa" />
              </button>
            )}
          </div>
          <div className="visite-filters">
            <FilterBtn active={filterMode === "all"} onClick={() => setFilterMode("all")}>
              <Layers size={11} /> {tr("filters.all", { count: artworks.length })}
            </FilterBtn>
            <FilterBtn active={filterMode === "damaged"} danger onClick={() => setFilterMode("damaged")}>
              <AlertTriangle size={11} /> {tr("filters.damaged", { count: stats.damaged })}
            </FilterBtn>
            <FilterBtn gold onClick={pickRandom}>
              <Shuffle size={11} /> {tr("filters.random")}
            </FilterBtn>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div style={{ padding: "16px 0 100px" }}>
        <div className="visite-inner">
          {loadingArt ? (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa", fontSize: 13 }}>
              {tr("inspection.loading")}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
              <Package size={30} color="#d0c8bc" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 13 }}>
                {artworks.length === 0
                  ? tr("inspection.empty")
                  : tr("inspection.noResults")}
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 12 }}>
                {tr("inspection.resultCount", { count: filtered.length })}
                {filterMode === "damaged" && <span style={{ color: "#e07070" }}> · {tr("inspection.sortedByCondition")}</span>}
              </div>
              <div className="visite-artwork-grid">
                {filtered.map((a) => (
                  <ArtworkRow
                    key={a.id}
                    artwork={a}
                    onSaveCondition={handleSaveCondition}
                    locationRisk={selectedLoc?.storage_risk}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bouton "Terminer la visite" fixe en bas */}
      {!loadingArt && artworks.length > 0 && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "#fff", borderTop: "1px solid #e8e0d4",
          padding: "10px 0",
        }}>
          <div className="visite-inner">
            <button onClick={handleFinishVisit} style={{
              width: "100%", padding: "13px 0",
              background: "#1a1a1a", color: "#fff",
              border: "none", borderRadius: 4,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <ClipboardList size={15} />
              {tr("actions.finish")}
              <span style={{ fontSize: 11, color: "#c9a961", marginLeft: 4 }}>
                {tr("stats.evaluatedProgress", { evaluated: evaluatedCount, total: artworks.length })}
              </span>
            </button>
          </div>
        </div>
      )}

      {showRandom && randomArtwork && (
        <RandomModal
          artwork={randomArtwork}
          onClose={() => setShowRandom(false)}
          onNext={pickRandom}
          onSaveCondition={handleSaveCondition}
        />
      )}
    </div>
  );
}
